import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import clsx from "clsx";
import { useTheme } from "next-themes";
import { formatFillQuantityWithNotation } from "./fillQuantityFormatter";
import {
  getFillQuantityCellStyling,
  getFillQuantityFromStore,
  getInstrumentTypeFromStore,
} from "./fillQuantityHelpers";
import {
  isFillCancelled,
  isFillLocked,
  getFillFromStore,
} from "../../fillStateHelpers";

/**
 * FillQuantityCellRenderer
 *
 * Displays the fill quantity value from the fills array with real-time Zustand updates.
 * For RP trades, applies bid/ask/mid styling (rounded input style).
 * For OP trades, displays value without special styling.
 *
 * Handles special states:
 * - Cancelled fills: Display historic quantity with strikethrough
 * - Locked fills (timestamp): Display current quantity with disabled styling
 *
 * Works in conjunction with fillQuantityValueFormatter for copy/paste support.
 */
const FillQuantityCellRenderer = (params: any) => {
  const { theme } = useTheme();

  // Subscribe to Zustand store for reactive updates (hooks must run before any early return)
  const currentTradeData = tradeDataStore((state) => state.tradeData);

  if (!params.data) {
    return null;
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const isRP = fullTradeData?.role === "RP";

  // Get fill details from params.data
  const fillId = params.data?.fillId;
  const blockId = params.data?.blockId;
  const extFillId = params.data?.fillData?.extFillId;

  // Get the full fill object from the store to check its state
  // extFillId fallback: when fillId is stale after backend assigns new fillId
  const fill =
    esid && fillId !== undefined && blockId !== undefined
      ? getFillFromStore(currentTradeData, esid, blockId, fillId, extFillId)
      : null;

  const isCancelled = fill ? isFillCancelled(fill) : false;
  const isEditableFillType =
    fill?.fillType === undefined ||
    fill?.fillType === "New" ||
    fill?.fillType === "Updated";

  const isLocked = fill ? isFillLocked(fill) : false;

  // Determine which quantity to display
  let displayQuantity: number | null = null;
  let instrumentType: string | undefined = undefined;

  if (isCancelled && fill?.historicFillSize !== undefined) {
    // Cancelled fills: show the historic quantity
    displayQuantity = fill.historicFillSize;
  } else if (esid && fillId !== undefined && blockId !== undefined) {
    // Normal fills: get fresh value from store
    displayQuantity = getFillQuantityFromStore(esid, fillId, blockId);
  }

  if (esid && blockId !== undefined) {
    instrumentType = getInstrumentTypeFromStore(esid, blockId);
  }

  // Fallback to params.value if store doesn't have it
  if (displayQuantity === null) {
    displayQuantity = params.value ?? null;
  }

  // Fallback instrumentType from params.data if not in store
  if (!instrumentType) {
    instrumentType = params.data?.instrumentType;
  }

  const formattedValue = formatFillQuantityWithNotation(
    displayQuantity,
    instrumentType,
  );
  const cellStyling = getFillQuantityCellStyling(isRP, theme);

  return (
    <div
      data-testid="fillQuantityCellContainer"
      id="fillQuantityCellContainer"
      className={clsx(
        "justify-center text-center",
        isRP &&
          !isLocked &&
          !isCancelled &&
          isEditableFillType &&
          `ag-input ag-roundedInputStyle border ${cellStyling}`,
        isLocked && "opacity-60",
      )}
      style={{
        width: "100%",
        ...(isCancelled && {
          textDecoration: "line-through",
          opacity: 0.5,
          color: "#888",
        }),
      }}
    >
      {formattedValue}
    </div>
  );
};

export default FillQuantityCellRenderer;
